/**
 * A live site with none of its pages live: readers who open / get the bare
 * article list (smagent-sites HomeView), with no menu when the header has no
 * links left. Pages and Today warn about it, and Pages offers to restore what
 * was deleted.
 *
 * Only a site that has (or recently had) pages is warned. One that never had
 * any is an article list on purpose.
 */

export interface GapPage {
  id?: string;
  title?: string;
  path?: string;
  state: { live?: unknown; scheduled?: unknown; takenDown?: unknown };
}

export interface GapDeleted {
  id: string;
  title: string;
  path: string | null;
  deletedAt: string;
  daysLeft: number;
  /** The page now at its address: restoring waits until it moves or goes. */
  takenBy?: { id: string; title: string | null; live: boolean } | null;
}

export interface HomeGap {
  /** false: the header has no links either. null: not known yet. */
  menu: boolean | null;
  /** Deleted pages that can still be restored (their address is free). */
  restorable: number;
  /** A new home page waiting to be published: the fix is reviewing it, not restoring. */
  homeDraft: { id: string; title: string; scheduled: boolean } | null;
}

/**
 * The page at the home address ("/": the working site settings' home page)
 * that isn't live yet but could be: drafted or scheduled, not taken down.
 */
export function homeDraft(
  pages: GapPage[],
  homeSlug: string | null | undefined
): HomeGap['homeDraft'] {
  if (!homeSlug) return null;
  const p = pages.find((x) => x.path === `/${homeSlug}`);
  if (!p?.id || p.state?.live || p.state?.takenDown) return null;
  return { id: p.id, title: p.title || 'Home', scheduled: Boolean(p.state?.scheduled) };
}

/** Today's and Pages' warning: what readers see, and the one fix that applies. */
export function homeNotice(gap: HomeGap): { text: string; label: string; to: string } {
  const sees = readersSee(gap);
  if (gap.homeDraft?.scheduled) {
    return {
      text: `A new home page is scheduled. Until it goes live, visitors see ${sees}.`,
      label: 'Review →',
      to: `/content?view=pages&page=${gap.homeDraft.id}`,
    };
  }
  if (gap.homeDraft) {
    return {
      text: `A new home page is waiting for you. Until you approve it, visitors see ${sees}.`,
      label: 'Review →',
      to: `/content?view=pages&page=${gap.homeDraft.id}`,
    };
  }
  return {
    text: `Your site has no home page. Visitors see ${sees}.`,
    label: gap.restorable ? 'Restore pages →' : 'Review pages →',
    to: '/content?view=pages',
  };
}

/** "Address now used by a new draft", for a deleted page whose address is taken. */
export const takenLine = (d: GapDeleted) =>
  d.takenBy ? `Address now used by ${d.takenBy.live ? 'another page' : 'a new draft'}` : null;

export function homeGap({
  publicEnabled,
  pages,
  deleted,
  navigation,
  homeSlug,
}: {
  publicEnabled: boolean | null | undefined;
  pages: GapPage[];
  deleted: GapDeleted[];
  navigation?: { header?: unknown[] | null; cta?: unknown } | null;
  homeSlug?: string | null;
}): HomeGap | null {
  if (!publicEnabled) return null;
  if (pages.some((p) => p.state?.live)) return null;
  if (!pages.length && !deleted.length) return null;
  return {
    menu: navigation ? Boolean(navigation.header?.length || navigation.cta) : null,
    restorable: deleted.filter((d) => !d.takenBy).length,
    homeDraft: homeDraft(pages, homeSlug),
  };
}

/** "Your latest articles, with no menu." — what a reader sees at / right now. */
export function readersSee(gap: HomeGap) {
  return `your latest articles${gap.menu === false ? ', with no menu' : ''}`;
}

const shortDay = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

/**
 * "Home, About and 7 more were deleted on Oct 9. You can restore them for 29
 * more days." The days are the soonest to run out.
 */
export function deletedSentence(items: GapDeleted[]) {
  if (!items.length) return '';
  const titles = items.map((d) => d.title || 'Untitled');
  const names =
    titles.length === 1
      ? titles[0]
      : titles.length <= 3
        ? `${titles.slice(0, -1).join(', ')} and ${titles[titles.length - 1]}`
        : `${titles.slice(0, 2).join(', ')} and ${titles.length - 2} more`;
  const days = items.map((d) => shortDay(d.deletedAt));
  const sorted = [...items].sort(
    (a, b) => new Date(a.deletedAt).getTime() - new Date(b.deletedAt).getTime()
  );
  const first = shortDay(sorted[0].deletedAt);
  const last = shortDay(sorted[sorted.length - 1].deletedAt);
  const when = new Set(days).size === 1 ? `on ${first}` : `between ${first} and ${last}`;
  const one = items.length === 1;
  const left = Math.min(...items.map((d) => d.daysLeft));
  const restore =
    left <= 0
      ? `Today is the last day to restore ${one ? 'it' : 'them'}.`
      : `You can restore ${one ? 'it' : 'them'} for ${left} more day${left === 1 ? '' : 's'}.`;
  return `${names} ${one ? 'was' : 'were'} deleted ${when}. ${restore}`;
}

/**
 * Parents before the pages inside them: the server refuses a page whose parent
 * is still deleted. Pages whose address a new page now has are left out: they
 * stay in history, and the new page keeps the address.
 */
export function restoreOrder<T extends { path: string | null; takenBy?: unknown }>(
  items: T[]
): T[] {
  const depth = (p: string | null) => (p || '').split('/').filter(Boolean).length;
  return items.filter((d) => !d.takenBy).sort((a, b) => depth(a.path) - depth(b.path));
}

/**
 * Under Recently deleted when some addresses are taken (Prototype v7): what
 * Restore does and doesn't do. "Home and About stay in history: new drafts now
 * use their addresses, and keep them. To bring one back, rename or delete the
 * new page first."
 */
export function takenNote(items: GapDeleted[]): string | null {
  const taken = items.filter((d) => d.takenBy);
  if (!taken.length) return null;
  const titles = taken.map((d) => d.title || 'Untitled');
  const names =
    titles.length === 1
      ? titles[0]
      : `${titles.slice(0, -1).join(', ')} and ${titles[titles.length - 1]}`;
  const one = taken.length === 1;
  const drafts = taken.every((d) => !d.takenBy?.live);
  const by = one
    ? drafts
      ? 'a new draft now uses its address'
      : 'another page now uses its address'
    : drafts
      ? 'new drafts now use their addresses'
      : 'other pages now use their addresses';
  return `${names} ${one ? 'stays' : 'stay'} in history: ${by}, and ${one ? 'keeps it' : 'keep them'}. To bring ${one ? 'it' : 'one'} back, rename or delete the new page first.`;
}
